import { BACKEND } from "@/lib/api";

// Frontend self-healing: UI error reports (no personal data), stale-bundle recovery, new-deploy detection.
const HEAL_AT = "fmo_heal_at";
const HEAL_COUNT = "fmo_heal_count";
const sent = new Map();
let pendingUpdate = false;

export const isPreviewHost = () =>
  /\.preview\.emergentagent\.com$|\.preview\.emergentcf\.cloud$|^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);

export const isChunkError = (err) => {
  const msg = String(err?.message || err || "");
  return err?.name === "ChunkLoadError" || /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg);
};

const clean = (s) => String(s || "").replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, "•").replace(/\b[0-9a-f]{12,}\b|\b\d{6,}\b/gi, "•").slice(0, 300);

export function reportUiError({ component = "?", message = "", kind = "crash" }) {
  const key = `${component}|${message}`;
  const now = Date.now();
  if (now - (sent.get(key) || 0) < 30000 || sent.size > 50) return;
  sent.set(key, now);
  const body = JSON.stringify({ component: clean(component).slice(0, 80), route: window.location.pathname, message: clean(message), kind });
  fetch(`${BACKEND}/api/selfheal/ui-error`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
}

const BUILD = (document.querySelector('script[src*="/static/js/main"]')?.getAttribute("src") || "").match(/main\.([0-9a-f]+)/)?.[1] || "dev";
const MAX_AUTO_RELOADS = 2;

// Old bundle after a republish: activate the waiting service worker, drop old shell caches, soft reload.
// Hard guard against loops: at most ONE automatic reload per bundle per session (and 2 in total); a blocked loop is journaled.
export async function healStale(reason) {
  const doneKey = `fmo_heal_done_${BUILD}`;
  const count = Number(sessionStorage.getItem(HEAL_COUNT) || 0);
  if (sessionStorage.getItem(doneKey) || count >= MAX_AUTO_RELOADS || Date.now() - Number(sessionStorage.getItem(HEAL_AT) || 0) < 60000) {
    reportUiError({ component: "app", message: `auto-reload blocked (${count} done): ${reason}`, kind: "loop" });
    return false;
  }
  sessionStorage.setItem(doneKey, String(Date.now()));
  sessionStorage.setItem(HEAL_AT, String(Date.now()));
  sessionStorage.setItem(HEAL_COUNT, String(count + 1));
  reportUiError({ component: "app", message: reason, kind: "chunk" });
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) || [];
    regs.forEach((r) => r.waiting?.postMessage({ type: "SKIP_WAITING" }));
    const keys = (await window.caches?.keys?.()) || [];
    await Promise.all(keys.filter((k) => k.startsWith("fmo-shell-")).map((k) => window.caches.delete(k)));
  } catch {
    /* reload anyway */
  }
  const u = new URL(window.location.href);
  u.searchParams.delete("fmo_fault");
  window.location.replace(u.toString());
  return true;
}


async function checkVersion() {
  try {
    const r = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return;
    const main = (await r.json())?.files?.["main.js"];
    // Frontend build id vs the deployed one (never the backend boot time).
    if (main && BUILD !== "dev" && !main.includes(`main.${BUILD}.`)) pendingUpdate = true;
  } catch {
    /* offline: next check */
  }
}

// Called on every route change: a detected new deploy is applied when the user navigates (soft reload).
export const applyPendingUpdate = () => pendingUpdate && healStale("new version deployed");

export function installSelfHealing() {
  const onError = (err, kind) => {
    if (isChunkError(err)) return healStale(String(err?.message || err));
    const msg = String(err?.message || err || "");
    if (!msg || /ResizeObserver|Script error\.?$/.test(msg)) return undefined;
    return reportUiError({ component: "window", message: msg, kind });
  };
  window.addEventListener("error", (e) => onError(e.error || e.message, "unhandled"));
  window.addEventListener("unhandledrejection", (e) => onError(e.reason, "unhandled"));
  if (process.env.NODE_ENV === "production") {
    setInterval(checkVersion, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && checkVersion());
  }
}
